/**
 * «Συσκευές ταμείου» — τύποι και καθαρές συναρτήσεις της οθόνης (χωρίς React), ώστε η σειρά των δοκιμών και τα
 * κείμενα κατάστασης να ελέγχονται με tests. Συμβόλαιο: backend/routes/cashierDevices.js + print-agent/lib/devices.js.
 */

export type DisplayMode = 'epson' | 'cd5220';
export type Encoding = 'cp737' | 'windows1253' | 'iso88597' | 'ascii' | 'latin';

export interface Port {
  path: string;
  label: string;
  manufacturer: string | null;
  friendlyName: string | null;
  usedBy: 'display' | 'callerId' | null;
}

export interface DisplayCfg {
  enabled: boolean;
  port?: string;
  baudRate?: number;
  mode?: DisplayMode;
  encoding?: Encoding;
  codePage?: number | null;
  id?: string | null;
  name?: string | null;
}

export interface CallerCfg {
  enabled: boolean;
  port?: string;
  baudRate?: number;
}

export type DeviceState = 'off' | 'starting' | 'ok' | 'error' | 'paused';

export interface LiveStatus {
  ok: true;
  uid: string;
  machine: string;
  version: string | null;
  paused: boolean;
  ports: Port[];
  display: { source: 'server' | 'env'; config: DisplayCfg | null; state: DeviceState; error: string | null; displayId: string | null };
  callerId: { source: 'server' | 'env'; config: CallerCfg | null; state: DeviceState; error: string | null; lastCallAt: number | null };
}

export interface Pc {
  uid: string | null;
  machine: string | null;
  version: string | null;
  supported: boolean;
  duplicate: boolean;
  connectedAt: string | null;
  printers: string[];
  saved: { display: DisplayCfg | null; callerId: CallerCfg | null } | null;
  live: LiveStatus | null;
  liveError: string | null;
}

export interface DevicesResponse {
  tableReady: boolean;
  customerDisplayEnabled: boolean;
  pcs: Pc[];
}

export interface Combo {
  n: number;
  port: string;
  baudRate: number;
  mode: DisplayMode;
}

/** Η πιο συνηθισμένη ρύθμιση πρώτη: 9600 (LD222, οι περισσότερες VFD220), μετά 2400, 19200, 4800. */
export const DISPLAY_SETTINGS: Array<{ baudRate: number; mode: DisplayMode }> = [
  { baudRate: 9600, mode: 'epson' }, { baudRate: 9600, mode: 'cd5220' },
  { baudRate: 2400, mode: 'epson' }, { baudRate: 2400, mode: 'cd5220' },
  { baudRate: 19200, mode: 'epson' }, { baudRate: 19200, mode: 'cd5220' },
  { baudRate: 4800, mode: 'epson' }, { baudRate: 4800, mode: 'cd5220' },
];

const USB_SERIAL = /prolific|ftdi|ch34|silicon|cp210|usb/i;
const MODEM = /modem|robotics|conexant|agere/i;

/**
 * Οι δοκιμές του «Βρες την οθόνη», αριθμημένες από το 1. Οι USB-σειριακές θύρες πρώτες (εκεί βρέθηκαν οι οθόνες
 * των Κουλουράδων και το LD222), όσες λέγονται «modem» τελευταίες (και USB να είναι, δεν είναι η οθόνη), και ποτέ
 * η θύρα που χρησιμοποιεί τώρα το modem.
 */
export function displayCombos(ports: Port[]): Combo[] {
  const rank = (p: Port) => {
    const name = `${p.manufacturer || ''} ${p.friendlyName || ''}`;
    return MODEM.test(name) ? 2 : USB_SERIAL.test(name) ? 0 : 1;
  };
  const candidates = ports
    .filter(p => p.usedBy !== 'callerId')
    .map((p, i) => ({ p, i, r: rank(p) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map(x => x.p);
  const combos: Combo[] = [];
  for (const p of candidates) {
    for (const s of DISPLAY_SETTINGS) combos.push({ n: combos.length + 1, port: p.path, ...s });
  }
  return combos;
}

/** Τι γράφεται στην οθόνη σε κάθε δοκιμή: μόνο λατινικά/ψηφία, ώστε να διαβάζεται όποια κι αν είναι η ρύθμιση. */
export const comboLines = (c: Combo): [string, string] => [`     >> ${c.n} <<`, `${c.port} ${c.baudRate}`];

/** Θύρα που δεν ανοίγει: οι υπόλοιπες ταχύτητές της θα αποτύχουν το ίδιο, οπότε προσπερνιούνται. */
export const isPortUnavailable = (error: string) => /access denied|busy|ebusy|enoent|file not found|cannot open|δεν υπάρχει/i.test(error);

/** Οι 5 δοκιμές ελληνικών (ίδιες με το vfd-probe --greek). */
export const GREEK_TRIALS: Array<{ n: number; encoding: Encoding; codePage: number | null; hint: string }> = [
  { n: 1, encoding: 'cp737', codePage: null, hint: 'συνηθισμένο (CP737)' },
  { n: 2, encoding: 'windows1253', codePage: null, hint: 'Windows-1253' },
  { n: 3, encoding: 'cp737', codePage: 14, hint: 'CP737 με εντολή πίνακα' },
  { n: 4, encoding: 'iso88597', codePage: 15, hint: 'ISO-8859-7 με εντολή πίνακα' },
  { n: 5, encoding: 'latin', codePage: null, hint: 'χωρίς ελληνικά — με λατινικά (KALIMERA SAS)' },
];
export const greekLines = (n: number): [string, string] => [`${n})`, 'ΚΑΛΗΜΕΡΑ ΣΑΣ'];

export const encodingLabel = (encoding?: string | null, codePage?: number | null): string => {
  const t = GREEK_TRIALS.find(g => g.encoding === encoding && (g.codePage ?? null) === (codePage ?? null));
  return t ? `τρόπος ${t.n} — ${t.hint}` : (encoding || '—');
};

export const modeLabel = (mode?: string | null) => (mode === 'cd5220' ? 'CD5220' : 'EPSON');

export type Tone = 'ok' | 'warn' | 'danger' | 'muted' | 'brand';

/** Μία φράση για την κατάσταση της οθόνης, όπως τη λέει ο τεχνικός στο τηλέφωνο. */
export function displayStatus(live: LiveStatus['display'] | null | undefined): { tone: Tone; text: string } {
  if (!live) return { tone: 'muted', text: 'Άγνωστη κατάσταση' };
  if (live.state === 'paused') return { tone: 'brand', text: 'Σε παύση — γίνεται ρύθμιση' };
  if (!live.config) return { tone: 'muted', text: 'Κλειστή' };
  if (live.state === 'ok') return { tone: 'ok', text: `Δουλεύει · ${live.config.port}` };
  if (live.state === 'error') return { tone: 'danger', text: `Πρόβλημα · ${live.error || 'άγνωστο σφάλμα'}` };
  return { tone: 'muted', text: `Ξεκινά · ${live.config.port}` };
}

export function callerStatus(live: LiveStatus['callerId'] | null | undefined): { tone: Tone; text: string } {
  if (!live) return { tone: 'muted', text: 'Άγνωστη κατάσταση' };
  if (live.state === 'paused') return { tone: 'brand', text: 'Σε παύση — γίνεται ρύθμιση' };
  if (!live.config) return { tone: 'muted', text: 'Κλειστή' };
  if (live.state === 'ok') return { tone: 'ok', text: `Έτοιμη · modem στη ${live.config.port}` };
  if (live.state === 'error') return { tone: 'danger', text: `Πρόβλημα · ${live.error || 'άγνωστο σφάλμα'}` };
  return { tone: 'muted', text: `Ξεκινά · ${live.config.port}` };
}

/** Τι κρατάμε όταν αλλάζει η μία συσκευή: την αποθηκευμένη ρύθμιση της άλλης (null = «από .env»). */
export function mergeSave(pc: Pc, patch: { display?: DisplayCfg | null; callerId?: CallerCfg | null }) {
  const saved = pc.saved || { display: null, callerId: null };
  return {
    display: patch.display !== undefined ? patch.display : saved.display,
    callerId: patch.callerId !== undefined ? patch.callerId : saved.callerId,
  };
}
